import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';

import { environment } from '../../../../environments/environment';
import { FileService } from '../../../core/services/file';
import { UserService, User } from '../../../core/services/user.service';
import { EncryptionService } from '../../../core/services/encryption';
import { AuthService } from '../../../core/services/auth';
import { SETTINGS } from '../../../core/config/common.settings';
import { TermsConditionsModalComponent } from '../terms-conditions-modal/terms-conditions-modal.component';

// Extended User type with additional properties from API response
type UserWithAccountSetup = User & {
  isFirstLogin?: boolean;
  accountStatus?: string;
  requiresAccountSetup?: boolean;
};

interface Country {
  code: string;
  name: string;
}

interface Citizenship {
  code: string;
  name: string;
}

@Component({
  selector: 'app-user-account-setup',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CardModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    PasswordModule,
    ToastModule,
    ProgressBarModule,
    TermsConditionsModalComponent
  ],
  providers: [MessageService],
  templateUrl: './user-account-setup.html',
  styleUrl: './user-account-setup.scss'
})
export class UserAccountSetup implements OnInit, OnDestroy {
  currentStep = 1;
  totalSteps = 2;
  loading = false;
  submitting = false;
  passwordSubmitAttempted = false; // Track if user attempted to submit password form
  personalInfoSubmitAttempted = false; // Track if user attempted to submit personal info form
  userId: string = '';
  user: UserWithAccountSetup | null = null;
  isFileUploading = false;
  uploadedFileUrl: string | null = null;
  uploadedFile: File | null = null; // Add this property to store the uploaded file
  dragOver: boolean = false; // Add drag over state for visual feedback
  showTermsModal: boolean = false;
  pendingRedirect: boolean = false;
  termsJustAccepted: boolean = false; // Flag to prevent modal from reappearing after acceptance
  redirecting: boolean = false; // Flag to show loader during redirect after terms acceptance

  // Field name mapping for better error messages
  fieldDisplayNames: { [key: string]: string } = {
    firstName: 'First name',
    lastName: 'Last name',
    gender: 'Gender',
    mobileNumber: 'Phone number',
    streetAddress: 'Street address',
    houseNumber: 'House number',
    postcode: 'Postcode',
    city: 'City',
    country: 'Country',
    citizenship: 'Citizenship',
    nationalInsuranceNo: 'National Insurance number',
    tradeLicence: 'Trade licence',
    idCard: 'ID card'
  };

  // Step 1: Password Setup
  passwordForm: FormGroup;

  // Step 2: Personal Information
  personalInfoForm: FormGroup;

  // Data for dropdowns
  countries: Country[] = [
    { code: 'AF', name: 'Afghanistan' },
    { code: 'AL', name: 'Albania' },
    { code: 'DZ', name: 'Algeria' },
    { code: 'AD', name: 'Andorra' },
    { code: 'AO', name: 'Angola' },
    { code: 'AG', name: 'Antigua and Barbuda' },
    { code: 'AR', name: 'Argentina' },
    { code: 'AM', name: 'Armenia' },
    { code: 'AU', name: 'Australia' },
    { code: 'AT', name: 'Austria' },
    { code: 'AZ', name: 'Azerbaijan' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'BB', name: 'Barbados' },
    { code: 'BY', name: 'Belarus' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BZ', name: 'Belize' },
    { code: 'BJ', name: 'Benin' },
    { code: 'BT', name: 'Bhutan' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'BW', name: 'Botswana' },
    { code: 'BR', name: 'Brazil' },
    { code: 'BN', name: 'Brunei' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'BF', name: 'Burkina Faso' },
    { code: 'BI', name: 'Burundi' },
    { code: 'CV', name: 'Cabo Verde' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'CM', name: 'Cameroon' },
    { code: 'CA', name: 'Canada' },
    { code: 'CF', name: 'Central African Republic' },
    { code: 'TD', name: 'Chad' },
    { code: 'CL', name: 'Chile' },
    { code: 'CN', name: 'China' },
    { code: 'CO', name: 'Colombia' },
    { code: 'KM', name: 'Comoros' },
    { code: 'CG', name: 'Congo' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'HR', name: 'Croatia' },
    { code: 'CU', name: 'Cuba' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'CD', name: 'Democratic Republic of the Congo' },
    { code: 'DK', name: 'Denmark' },
    { code: 'DJ', name: 'Djibouti' },
    { code: 'DM', name: 'Dominica' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'EG', name: 'Egypt' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'GQ', name: 'Equatorial Guinea' },
    { code: 'ER', name: 'Eritrea' },
    { code: 'EE', name: 'Estonia' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'FJ', name: 'Fiji' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'GA', name: 'Gabon' },
    { code: 'GM', name: 'Gambia' },
    { code: 'GE', name: 'Georgia' },
    { code: 'DE', name: 'Germany' },
    { code: 'GH', name: 'Ghana' },
    { code: 'GR', name: 'Greece' },
    { code: 'GD', name: 'Grenada' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'GN', name: 'Guinea' },
    { code: 'GW', name: 'Guinea-Bissau' },
    { code: 'GY', name: 'Guyana' },
    { code: 'HT', name: 'Haiti' },
    { code: 'HN', name: 'Honduras' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IS', name: 'Iceland' },
    { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'IR', name: 'Iran' },
    { code: 'IQ', name: 'Iraq' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IL', name: 'Israel' },
    { code: 'IT', name: 'Italy' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'JP', name: 'Japan' },
    { code: 'JO', name: 'Jordan' },
    { code: 'KZ', name: 'Kazakhstan' },
    { code: 'KE', name: 'Kenya' },
    { code: 'KI', name: 'Kiribati' },
    { code: 'KP', name: 'North Korea' },
    { code: 'KR', name: 'South Korea' },
    { code: 'KW', name: 'Kuwait' },
    { code: 'KG', name: 'Kyrgyzstan' },
    { code: 'LA', name: 'Laos' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LB', name: 'Lebanon' },
    { code: 'LS', name: 'Lesotho' },
    { code: 'LR', name: 'Liberia' },
    { code: 'LY', name: 'Libya' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MG', name: 'Madagascar' },
    { code: 'MW', name: 'Malawi' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'MV', name: 'Maldives' },
    { code: 'ML', name: 'Mali' },
    { code: 'MT', name: 'Malta' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'MR', name: 'Mauritania' },
    { code: 'MU', name: 'Mauritius' },
    { code: 'MX', name: 'Mexico' },
    { code: 'FM', name: 'Micronesia' },
    { code: 'MD', name: 'Moldova' },
    { code: 'MC', name: 'Monaco' },
    { code: 'MN', name: 'Mongolia' },
    { code: 'ME', name: 'Montenegro' },
    { code: 'MA', name: 'Morocco' },
    { code: 'MZ', name: 'Mozambique' },
    { code: 'MM', name: 'Myanmar' },
    { code: 'NA', name: 'Namibia' },
    { code: 'NR', name: 'Nauru' },
    { code: 'NP', name: 'Nepal' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'NE', name: 'Niger' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'MK', name: 'North Macedonia' },
    { code: 'NO', name: 'Norway' },
    { code: 'OM', name: 'Oman' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'PW', name: 'Palau' },
    { code: 'PA', name: 'Panama' },
    { code: 'PG', name: 'Papua New Guinea' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'PE', name: 'Peru' },
    { code: 'PH', name: 'Philippines' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'QA', name: 'Qatar' },
    { code: 'RO', name: 'Romania' },
    { code: 'RU', name: 'Russia' },
    { code: 'RW', name: 'Rwanda' },
    { code: 'KN', name: 'Saint Kitts and Nevis' },
    { code: 'LC', name: 'Saint Lucia' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines' },
    { code: 'WS', name: 'Samoa' },
    { code: 'SM', name: 'San Marino' },
    { code: 'ST', name: 'Sao Tome and Principe' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SN', name: 'Senegal' },
    { code: 'RS', name: 'Serbia' },
    { code: 'SC', name: 'Seychelles' },
    { code: 'SL', name: 'Sierra Leone' },
    { code: 'SG', name: 'Singapore' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'SB', name: 'Solomon Islands' },
    { code: 'SO', name: 'Somalia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'SS', name: 'South Sudan' },
    { code: 'ES', name: 'Spain' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'SD', name: 'Sudan' },
    { code: 'SR', name: 'Suriname' },
    { code: 'SZ', name: 'Eswatini' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'SY', name: 'Syria' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'TJ', name: 'Tajikistan' },
    { code: 'TZ', name: 'Tanzania' },
    { code: 'TH', name: 'Thailand' },
    { code: 'TL', name: 'Timor-Leste' },
    { code: 'TG', name: 'Togo' },
    { code: 'TO', name: 'Tonga' },
    { code: 'TT', name: 'Trinidad and Tobago' },
    { code: 'TN', name: 'Tunisia' },
    { code: 'TR', name: 'Turkey' },
    { code: 'TM', name: 'Turkmenistan' },
    { code: 'TV', name: 'Tuvalu' },
    { code: 'UG', name: 'Uganda' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'UZ', name: 'Uzbekistan' },
    { code: 'VU', name: 'Vanuatu' },
    { code: 'VA', name: 'Vatican City' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'YE', name: 'Yemen' },
    { code: 'ZM', name: 'Zambia' },
    { code: 'ZW', name: 'Zimbabwe' }
  ];

  citizenships: Citizenship[] = [
    { code: 'AF', name: 'Afghan' },
    { code: 'AL', name: 'Albanian' },
    { code: 'DZ', name: 'Algerian' },
    { code: 'AD', name: 'Andorran' },
    { code: 'AO', name: 'Angolan' },
    { code: 'AG', name: 'Antiguan' },
    { code: 'AR', name: 'Argentine' },
    { code: 'AM', name: 'Armenian' },
    { code: 'AU', name: 'Australian' },
    { code: 'AT', name: 'Austrian' },
    { code: 'AZ', name: 'Azerbaijani' },
    { code: 'BS', name: 'Bahamian' },
    { code: 'BH', name: 'Bahraini' },
    { code: 'BD', name: 'Bangladeshi' },
    { code: 'BB', name: 'Barbadian' },
    { code: 'BY', name: 'Belarusian' },
    { code: 'BE', name: 'Belgian' },
    { code: 'BZ', name: 'Belizean' },
    { code: 'BJ', name: 'Beninese' },
    { code: 'BT', name: 'Bhutanese' },
    { code: 'BO', name: 'Bolivian' },
    { code: 'BA', name: 'Bosnian' },
    { code: 'BW', name: 'Botswanan' },
    { code: 'BR', name: 'Brazilian' },
    { code: 'BN', name: 'Bruneian' },
    { code: 'BG', name: 'Bulgarian' },
    { code: 'BF', name: 'Burkinabe' },
    { code: 'BI', name: 'Burundian' },
    { code: 'CV', name: 'Cape Verdean' },
    { code: 'KH', name: 'Cambodian' },
    { code: 'CM', name: 'Cameroonian' },
    { code: 'CA', name: 'Canadian' },
    { code: 'CF', name: 'Central African' },
    { code: 'TD', name: 'Chadian' },
    { code: 'CL', name: 'Chilean' },
    { code: 'CN', name: 'Chinese' },
    { code: 'CO', name: 'Colombian' },
    { code: 'KM', name: 'Comorian' },
    { code: 'CG', name: 'Congolese' },
    { code: 'CR', name: 'Costa Rican' },
    { code: 'HR', name: 'Croatian' },
    { code: 'CU', name: 'Cuban' },
    { code: 'CY', name: 'Cypriot' },
    { code: 'CZ', name: 'Czech' },
    { code: 'CD', name: 'Congolese' },
    { code: 'DK', name: 'Danish' },
    { code: 'DJ', name: 'Djiboutian' },
    { code: 'DM', name: 'Dominican' },
    { code: 'DO', name: 'Dominican' },
    { code: 'EC', name: 'Ecuadorian' },
    { code: 'EG', name: 'Egyptian' },
    { code: 'SV', name: 'Salvadoran' },
    { code: 'GQ', name: 'Equatorial Guinean' },
    { code: 'ER', name: 'Eritrean' },
    { code: 'EE', name: 'Estonian' },
    { code: 'ET', name: 'Ethiopian' },
    { code: 'FJ', name: 'Fijian' },
    { code: 'FI', name: 'Finnish' },
    { code: 'FR', name: 'French' },
    { code: 'GA', name: 'Gabonese' },
    { code: 'GM', name: 'Gambian' },
    { code: 'GE', name: 'Georgian' },
    { code: 'DE', name: 'German' },
    { code: 'GH', name: 'Ghanaian' },
    { code: 'GR', name: 'Greek' },
    { code: 'GD', name: 'Grenadian' },
    { code: 'GT', name: 'Guatemalan' },
    { code: 'GN', name: 'Guinean' },
    { code: 'GW', name: 'Guinea-Bissauan' },
    { code: 'GY', name: 'Guyanese' },
    { code: 'HT', name: 'Haitian' },
    { code: 'HN', name: 'Honduran' },
    { code: 'HU', name: 'Hungarian' },
    { code: 'IS', name: 'Icelandic' },
    { code: 'IN', name: 'Indian' },
    { code: 'ID', name: 'Indonesian' },
    { code: 'IR', name: 'Iranian' },
    { code: 'IQ', name: 'Iraqi' },
    { code: 'IE', name: 'Irish' },
    { code: 'IL', name: 'Israeli' },
    { code: 'IT', name: 'Italian' },
    { code: 'JM', name: 'Jamaican' },
    { code: 'JP', name: 'Japanese' },
    { code: 'JO', name: 'Jordanian' },
    { code: 'KZ', name: 'Kazakh' },
    { code: 'KE', name: 'Kenyan' },
    { code: 'KI', name: 'I-Kiribati' },
    { code: 'KP', name: 'North Korean' },
    { code: 'KR', name: 'South Korean' },
    { code: 'KW', name: 'Kuwaiti' },
    { code: 'KG', name: 'Kyrgyz' },
    { code: 'LA', name: 'Laotian' },
    { code: 'LV', name: 'Latvian' },
    { code: 'LB', name: 'Lebanese' },
    { code: 'LS', name: 'Basotho' },
    { code: 'LR', name: 'Liberian' },
    { code: 'LY', name: 'Libyan' },
    { code: 'LI', name: 'Liechtensteiner' },
    { code: 'LT', name: 'Lithuanian' },
    { code: 'LU', name: 'Luxembourger' },
    { code: 'MG', name: 'Malagasy' },
    { code: 'MW', name: 'Malawian' },
    { code: 'MY', name: 'Malaysian' },
    { code: 'MV', name: 'Maldivian' },
    { code: 'ML', name: 'Malian' },
    { code: 'MT', name: 'Maltese' },
    { code: 'MH', name: 'Marshallese' },
    { code: 'MR', name: 'Mauritanian' },
    { code: 'MU', name: 'Mauritian' },
    { code: 'MX', name: 'Mexican' },
    { code: 'FM', name: 'Micronesian' },
    { code: 'MD', name: 'Moldovan' },
    { code: 'MC', name: 'Monacan' },
    { code: 'MN', name: 'Mongolian' },
    { code: 'ME', name: 'Montenegrin' },
    { code: 'MA', name: 'Moroccan' },
    { code: 'MZ', name: 'Mozambican' },
    { code: 'MM', name: 'Burmese' },
    { code: 'NA', name: 'Namibian' },
    { code: 'NR', name: 'Nauruan' },
    { code: 'NP', name: 'Nepalese' },
    { code: 'NL', name: 'Dutch' },
    { code: 'NZ', name: 'New Zealander' },
    { code: 'NI', name: 'Nicaraguan' },
    { code: 'NE', name: 'Nigerien' },
    { code: 'NG', name: 'Nigerian' },
    { code: 'MK', name: 'Macedonian' },
    { code: 'NO', name: 'Norwegian' },
    { code: 'OM', name: 'Omani' },
    { code: 'PK', name: 'Pakistani' },
    { code: 'PW', name: 'Palauan' },
    { code: 'PA', name: 'Panamanian' },
    { code: 'PG', name: 'Papua New Guinean' },
    { code: 'PY', name: 'Paraguayan' },
    { code: 'PE', name: 'Peruvian' },
    { code: 'PH', name: 'Filipino' },
    { code: 'PL', name: 'Polish' },
    { code: 'PT', name: 'Portuguese' },
    { code: 'QA', name: 'Qatari' },
    { code: 'RO', name: 'Romanian' },
    { code: 'RU', name: 'Russian' },
    { code: 'RW', name: 'Rwandan' },
    { code: 'KN', name: 'Kittitian' },
    { code: 'LC', name: 'Saint Lucian' },
    { code: 'VC', name: 'Vincentian' },
    { code: 'WS', name: 'Samoan' },
    { code: 'SM', name: 'Sammarinese' },
    { code: 'ST', name: 'Sao Tomean' },
    { code: 'SA', name: 'Saudi' },
    { code: 'SN', name: 'Senegalese' },
    { code: 'RS', name: 'Serbian' },
    { code: 'SC', name: 'Seychellois' },
    { code: 'SL', name: 'Sierra Leonean' },
    { code: 'SG', name: 'Singaporean' },
    { code: 'SK', name: 'Slovak' },
    { code: 'SI', name: 'Slovenian' },
    { code: 'SB', name: 'Solomon Islander' },
    { code: 'SO', name: 'Somali' },
    { code: 'ZA', name: 'South African' },
    { code: 'SS', name: 'South Sudanese' },
    { code: 'ES', name: 'Spanish' },
    { code: 'LK', name: 'Sri Lankan' },
    { code: 'SD', name: 'Sudanese' },
    { code: 'SR', name: 'Surinamese' },
    { code: 'SZ', name: 'Swazi' },
    { code: 'SE', name: 'Swedish' },
    { code: 'CH', name: 'Swiss' },
    { code: 'SY', name: 'Syrian' },
    { code: 'TW', name: 'Taiwanese' },
    { code: 'TJ', name: 'Tajik' },
    { code: 'TZ', name: 'Tanzanian' },
    { code: 'TH', name: 'Thai' },
    { code: 'TL', name: 'Timorese' },
    { code: 'TG', name: 'Togolese' },
    { code: 'TO', name: 'Tongan' },
    { code: 'TT', name: 'Trinidadian' },
    { code: 'TN', name: 'Tunisian' },
    { code: 'TR', name: 'Turkish' },
    { code: 'TM', name: 'Turkmen' },
    { code: 'TV', name: 'Tuvaluan' },
    { code: 'UG', name: 'Ugandan' },
    { code: 'UA', name: 'Ukrainian' },
    { code: 'AE', name: 'Emirati' },
    { code: 'GB', name: 'British' },
    { code: 'US', name: 'American' },
    { code: 'UY', name: 'Uruguayan' },
    { code: 'UZ', name: 'Uzbek' },
    { code: 'VU', name: 'Ni-Vanuatu' },
    { code: 'VA', name: 'Vatican' },
    { code: 'VE', name: 'Venezuelan' },
    { code: 'VN', name: 'Vietnamese' },
    { code: 'YE', name: 'Yemeni' },
    { code: 'ZM', name: 'Zambian' },
    { code: 'ZW', name: 'Zimbabwean' }
  ];

  titles = [
    { label: 'Mr.', value: 'Mr' },
    { label: 'Mrs.', value: 'Mrs' },
    { label: 'Ms.', value: 'Ms' },
    { label: 'Miss', value: 'Miss' },
    { label: 'Dr.', value: 'Dr' },
    { label: 'Prof.', value: 'Prof' },
    { label: 'Sir', value: 'Sir' },
    { label: 'Lady', value: 'Lady' },
    { label: 'Lord', value: 'Lord' },
    { label: 'Dame', value: 'Dame' },
    { label: 'Rev.', value: 'Rev' },
    { label: 'Fr.', value: 'Fr' },
    { label: 'Sr.', value: 'Sr' },
    { label: 'Br.', value: 'Br' },
    { label: 'Capt.', value: 'Capt' },
    { label: 'Col.', value: 'Col' },
    { label: 'Gen.', value: 'Gen' },
    { label: 'Lt.', value: 'Lt' },
    { label: 'Maj.', value: 'Maj' },
    { label: 'Sgt.', value: 'Sgt' },
    { label: 'Adm.', value: 'Adm' },
    { label: 'Amb.', value: 'Amb' },
    { label: 'Atty.', value: 'Atty' },
    { label: 'Eng.', value: 'Eng' },
    { label: 'Hon.', value: 'Hon' },
    { label: 'Pres.', value: 'Pres' },
    { label: 'Gov.', value: 'Gov' },
    { label: 'Sen.', value: 'Sen' },
    { label: 'Rep.', value: 'Rep' },
    { label: 'Judge', value: 'Judge' },
    { label: 'Justice', value: 'Justice' },
    { label: 'Bishop', value: 'Bishop' },
    { label: 'Archbishop', value: 'Archbishop' },
    { label: 'Cardinal', value: 'Cardinal' },
    { label: 'Pope', value: 'Pope' },
    { label: 'Imam', value: 'Imam' },
    { label: 'Rabbi', value: 'Rabbi' },
    { label: 'Swami', value: 'Swami' },
    { label: 'Guru', value: 'Guru' },
    { label: 'Sensei', value: 'Sensei' },
    { label: 'Master', value: 'Master' },
    { label: 'Chief', value: 'Chief' },
    { label: 'Sheikh', value: 'Sheikh' },
    { label: 'Emir', value: 'Emir' },
    { label: 'Sultan', value: 'Sultan' },
    { label: 'King', value: 'King' },
    { label: 'Queen', value: 'Queen' },
    { label: 'Prince', value: 'Prince' },
    { label: 'Princess', value: 'Princess' },
    { label: 'Duke', value: 'Duke' },
    { label: 'Duchess', value: 'Duchess' },
    { label: 'Earl', value: 'Earl' },
    { label: 'Count', value: 'Count' },
    { label: 'Countess', value: 'Countess' },
    { label: 'Viscount', value: 'Viscount' },
    { label: 'Baron', value: 'Baron' },
    { label: 'Baroness', value: 'Baroness' },
    { label: 'Marquis', value: 'Marquis' },
    { label: 'Marquess', value: 'Marquess' },
    { label: 'Knight', value: 'Knight' },
    { label: 'Esq.', value: 'Esq' }
  ];

  genders = [
    { label: 'Male', value: 'MALE' },
    { label: 'Female', value: 'FEMALE' },
    { label: 'Non-binary', value: 'NON_BINARY' }
  ];

  tradeLicences = [
    { label: 'Yes', value: 'YES' },
    { label: 'No', value: 'NO' }
  ];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private fileService: FileService,
    private userService: UserService,
    private encryption: EncryptionService,
    private authService: AuthService
  ) {
    this.passwordForm = this.fb.group({
      password: ['', [
        Validators.required,
        Validators.minLength(SETTINGS.PASSWORD.MIN_LENGTH),
        Validators.pattern(SETTINGS.PASSWORD.VALIDATION_REGEX)
      ]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    this.personalInfoForm = this.fb.group({
      title: ['Mr'],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      gender: ['', Validators.required],
      mobileNumber: ['', [Validators.required, Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
      streetAddress: ['', Validators.required],
      houseNumber: ['', Validators.required],
      postcode: ['', Validators.required],
      city: ['', Validators.required],
      country: ['', Validators.required],
      citizenship: ['', Validators.required],
      nationalInsuranceNo: ['', Validators.required],
      taxNumber: [''],
      tradeLicence: ['', Validators.required],
      idCard: [null, Validators.required]
    });
  }

  ngOnInit() {
    // Check for step query parameter from login redirect
    this.route.queryParams.subscribe(params => {
      const step = params['step'];
      if (step) {
        this.currentStep = parseInt(step);
      }
    });
    
    // Always load the current user
    this.loadUser();
  }

  onTermsAccepted(): void {
    // Set flags to prevent modal from reappearing and show loader
    this.termsJustAccepted = true;
    this.redirecting = true;
    this.showTermsModal = false;
    this.pendingRedirect = false;
    
    // Save terms acceptance
    this.userService.acceptTermsAndConditions()
      .subscribe({
        next: (response: any) => {
          // Update local user object with the updated user from response
          if (response?.user) {
            this.authService.setLoggedInUser(JSON.stringify(response.user));
            // Update local user object
            this.user = { ...this.user, ...response.user, termsAccepted: true };
          }
          
          // For SA users, redirect directly to payment/sub page
          // Skip subscription checks and other intermediate steps
          setTimeout(() => {
            this.router.navigate(['/payment/sub']).then(() => {
              // Reset redirecting flag after navigation completes
              this.redirecting = false;
            }).catch((error) => {
              console.error('Navigation error:', error);
              this.redirecting = false;
            });
          }, 500); // Small delay to ensure terms are saved
        },
        error: (error) => {
          console.error('Error accepting terms:', error);
          this.termsJustAccepted = false; // Reset flag on error
          this.redirecting = false; // Hide loader on error
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save terms acceptance. Please try again.'
          });
          // Don't redirect on error
          this.pendingRedirect = false;
        }
      });
  }

  onTermsModalVisibleChange(visible: boolean): void {
    // If modal is closed without accepting (shouldn't happen with closable=false, but safety check)
    if (!visible && this.pendingRedirect) {
      // User closed modal without accepting - prevent redirect
      this.pendingRedirect = false;
      this.messageService.add({
        severity: 'warn',
        summary: 'Terms Required',
        detail: 'You must accept the Terms and Conditions to proceed.'
      });
      // Reopen the modal
      setTimeout(() => {
        this.showTermsModal = true;
      }, 100);
    }
  }

  loadUser() {
    this.loading = true;
    this.http.get<{message: string, user: UserWithAccountSetup}>(`${environment.apiUrl}/users/me`)
      .subscribe({
        next: (response) => {
          this.user = response.user;
          
          // Check if user needs account setup based on accountSetupStep
          if (this.user?.accountSetupStep === 1) {
            // Step 1: Password Setup - show password setup
            if (!this.route.snapshot.queryParams['step']) {
              this.currentStep = 1;
            }
            this.preFillForm();
          } else if (this.user?.accountSetupStep === 2) {
            // Step 2: Personal Information - show personal info form
            if (!this.route.snapshot.queryParams['step']) {
              this.currentStep = 2;
            }
            this.preFillForm();
          } else if (this.user?.accountSetupStep === 3) {
            // Step 3: Setup complete - check if SA user needs to accept terms
            const userRole = this.user?.roleId?.identification;
            // Don't show modal if terms were just accepted or if we're redirecting (prevents flicker)
            if (userRole === 'SA' && !this.user?.termsAccepted && !this.termsJustAccepted && !this.redirecting) {
              // SA user hasn't accepted terms yet - show terms modal
              this.pendingRedirect = true;
              setTimeout(() => {
                this.showTermsModal = true;
              }, 500);
              this.loading = false;
              return;
            }
            // If redirecting, don't process further - let the redirect happen
            if (this.redirecting) {
              this.loading = false;
              return;
            }
            // User has completed setup and (if SA) accepted terms
            // If user is APPROVED, they shouldn't be here - redirect to dashboard
            if (this.user?.status === 'APPROVED') {
              // Approved users should not land on this page
              // Redirect to appropriate dashboard (subscription check will be done by AuthGuard)
              this.router.navigate([this.getDashboardRouteByRole(userRole)]);
            } else {
              // Non-approved user - redirect to pending approval
              this.router.navigate(['/account-pending-approval']);
            }
            return;
          } else if (this.user?.requiresAccountSetup) {
            // Fallback for legacy logic - show password setup
            if (!this.route.snapshot.queryParams['step']) {
              this.currentStep = 1;
            }
            this.preFillForm();
          } else if (this.user?.isFirstLogin && this.user?.accountStatus === 'PENDING') {
            // User has already filled the form but status is still pending
            if (!this.route.snapshot.queryParams['step']) {
              this.currentStep = 2;
            }
            this.preFillForm();
            this.messageService.add({
              severity: 'info',
              summary: 'Account Setup',
              detail: 'Your account setup request has been submitted and is pending approval. You cannot edit or resubmit the form.'
            });
          } else if (!this.user?.isFirstLogin) {
            // User has already completed first login - redirect to appropriate dashboard
            this.router.navigate([this.getDashboardRouteByRole(this.user?.roleId?.identification)]);
            return;
          } else {
            // Default case - show password setup
            if (!this.route.snapshot.queryParams['step']) {
              this.currentStep = 1;
            }
            this.preFillForm();
          }
          
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading user:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load user information.',
            life: 5000
          });
          this.loading = false;
        }
      });
  }

  preFillForm() {
    if (this.user) {
      // Pre-fill form with existing user data
      this.personalInfoForm.patchValue({
        firstName: this.user.firstName || '',
        lastName: this.user.lastName || '',
        title: this.user.title || '',
        gender: this.user.gender || '',
        mobileNumber: this.user.mobileNumber || '',
        streetAddress: this.user.address?.streetAddress || '',
        houseNumber: this.user.address?.houseNumber || '',
        postcode: this.user.address?.postcode || '',
        city: this.user.address?.city || '',
        country: this.user.address?.country || '',
        citizenship: this.user.citizenship || '',
        nationalInsuranceNo: this.user.nationalInsuranceNo || '',
        taxNumber: this.user.taxNumber || '',
        tradeLicence: this.user.tradeLicence || '',
        idCard: this.user.idCard || ''
      });

      // If user has already submitted the form and it's pending approval, disable the form
      if (this.user.isFirstLogin && this.user.accountStatus === 'PENDING' && !this.user.requiresAccountSetup) {
        this.personalInfoForm.disable();
      }
    }
  }

  passwordMatchValidator(group: FormGroup) {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  onPasswordSubmit() {
    // Mark submit as attempted to show validation errors
    this.passwordSubmitAttempted = true;
    
    // Mark all fields as touched to show validation errors (only after submit attempt)
    this.markFormGroupTouched(this.passwordForm);
    
    if (this.passwordForm.valid) {
      this.submitting = true;
      const passwordData = {
        currentPassword: '', // For first-time setup, current password is empty
        newPassword: this.passwordForm.get('password')?.value
      };

      this.userService.changeCurrentUserPasswordFirstTime(passwordData)
        .subscribe({
          next: (response: any) => {
            // Update accountSetupStep to 2 (Step 2: Personal Information)
            this.updateAccountSetupStep(2);
            
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Password set successfully!'
            });
            this.currentStep = 2;
            this.submitting = false;
          },
          error: (error) => {
            console.error('Error setting password:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.error?.message || 'Failed to set password. Please try again.'
            });
            this.submitting = false;
          }
        });
    } else {
      this.markFormGroupTouched(this.passwordForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly.'
      });
    }
  }

  onPersonalInfoSubmit() {
    // Mark submit as attempted to show validation errors
    this.personalInfoSubmitAttempted = true;
    
    // Mark all fields as touched to show validation errors (only after submit attempt)
    this.markFormGroupTouched(this.personalInfoForm);
    
    if (this.personalInfoForm.valid) {
      this.submitting = true;
      const formData = this.personalInfoForm.value;

      // Prepare the data for the API
      const personalData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.title,
        gender: formData.gender,
        mobileNumber: formData.mobileNumber,
        address: {
          streetAddress: formData.streetAddress,
          houseNumber: formData.houseNumber,
          postcode: formData.postcode,
          city: formData.city,
          country: formData.country
        },
        citizenship: formData.citizenship,
        nationalInsuranceNo: formData.nationalInsuranceNo,
        taxNumber: formData.taxNumber,
        tradeLicence: formData.tradeLicence,
        idCard: this.uploadedFileUrl || formData.idCard
      };

      this.userService.completeUserRegistration(personalData)
        .subscribe({
          next: (response: any) => {
            // Update accountSetupStep to 3 (Setup Complete)
            this.updateAccountSetupStep(3);
            
            // Mark first login as complete
              this.userService.markFirstLoginComplete()
              .subscribe({
                next: () => {
                  this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Account setup completed successfully! Your account is pending approval.'
                  });
                  this.submitting = false;
                  
                  // Check user role and redirect accordingly
                  const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
                  if (encryptedUserStr) {
                    try {
                      const userStr = this.encryption.decrypt(encryptedUserStr);
                      const user = JSON.parse(userStr);
                      const userRole = user?.role?.identification;
                      
                      if (userRole === 'SA') {
                        // Show terms and conditions modal before redirecting to Chargebee
                        this.pendingRedirect = true;
                        setTimeout(() => {
                          this.showTermsModal = true;
                        }, 1500);
                      } else {
                        setTimeout(() => {
                          this.router.navigate(['/account-pending-approval']);
                        }, 1000);
                      }
                    } catch (error) {
                      console.error('Error parsing user data:', error);
                      // Default redirect if user data is corrupted
                      setTimeout(() => {
                        this.router.navigate(['/account-pending-approval']);
                      }, 2000);
                    }
                  } else {
                    // Default redirect if no user data found
                    setTimeout(() => {
                      this.router.navigate(['/account-pending-approval']);
                    }, 2000);
                  }
                },
                error: (error) => {
                  console.error('Error marking first login as complete:', error);
                  this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Account setup completed but there was an issue updating your login status.'
                  });
                  this.submitting = false;
                }
              });
          },
          error: (error) => {
            console.error('Error completing registration:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.error?.message || 'Failed to complete registration. Please try again.'
            });
            this.submitting = false;
          }
        });
    } else {
      this.markFormGroupTouched(this.personalInfoForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly.'
      });
      
      // Scroll to error summary on mobile/after short delay
      setTimeout(() => {
        const errorSummary = document.querySelector('.error-summary-container');
        if (errorSummary) {
          errorSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private updateAccountSetupStep(step: number) {
    // Update the accountSetupStep in the backend
    this.userService.updateCurrentUserProfile({ accountSetupStep: step })
      .subscribe({
        next: (response) => {
          console.log('Account setup step updated successfully to:', step);
          // Update the local user object
          if (this.user) {
            this.user.accountSetupStep = step;
          }
        },
        error: (error) => {
          console.error('Error updating account setup step:', error);
          // Don't show error to user as this is not critical for the main flow
        }
      });
  }

    getFieldError(formGroup: FormGroup, fieldName: string): string {
    const field = formGroup.get(fieldName);
    // Show error only if field is invalid and submit was attempted for personal info form
    if (field?.invalid && this.personalInfoSubmitAttempted) {
      // Get the display name for the field, with fallback to a formatted version
      const displayName = this.fieldDisplayNames[fieldName] || this.formatFieldName(fieldName);
      
      if (field.errors?.['required']) {
        return `${displayName} is required`;
      }
      if (field.errors?.['minlength']) {
        return `${displayName} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors?.['pattern']) {
        if (fieldName === 'password') {
          return 'Password must contain at least 8 characters, including uppercase, lowercase & number';
        }
        if (fieldName === 'mobileNumber') {
          return 'Please enter a valid phone number';
        }
      }
    }
    return '';
  }

  // Get list of all missing/invalid fields
  getMissingFields(): string[] {
    const missingFields: string[] = [];
    const form = this.personalInfoForm;
    
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control && control.invalid && this.personalInfoSubmitAttempted) {
        const displayName = this.fieldDisplayNames[key] || this.formatFieldName(key);
        missingFields.push(displayName);
      }
    });
    
    return missingFields;
  }

  // Check if a field is invalid (for highlighting)
  isFieldInvalid(fieldName: string): boolean {
    const field = this.personalInfoForm.get(fieldName);
    return !!(field && field.invalid && this.personalInfoSubmitAttempted);
  }

  // Helper method to format field names that aren't in our mapping
  private formatFieldName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim();
  }

  getPasswordError(): string {
    const passwordField = this.passwordForm.get('password');
    if (passwordField?.invalid && this.passwordSubmitAttempted) {
      if (passwordField.errors?.['required']) {
        return SETTINGS.PASSWORD_ERROR_MESSAGES.REQUIRED;
      }
      if (passwordField.errors?.['minlength']) {
        return `Password must be at least ${SETTINGS.PASSWORD.MIN_LENGTH} characters`;
      }
      if (passwordField.errors?.['pattern']) {
        return SETTINGS.PASSWORD_ERROR_MESSAGES.INVALID_FORMAT;
      }
    }
    return '';
  }

  getConfirmPasswordError(): string {
    const confirmPasswordField = this.passwordForm.get('confirmPassword');
    if (confirmPasswordField?.invalid && this.passwordSubmitAttempted) {
      if (confirmPasswordField.errors?.['required']) {
        return 'Confirm password is required';
      }
    }
    if (this.passwordForm.errors?.['passwordMismatch'] && this.passwordSubmitAttempted) {
      return 'Passwords do not match';
    }
    return '';
  }

  // Get password strength
  getPasswordStrength(password: string): { score: number; label: string; color: string } {
    if (!password) return { score: 0, label: 'Enter password', color: 'bg-gray-300' };
    
    let score = 0;
    if (password.length >= SETTINGS.PASSWORD.MIN_LENGTH) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[\W_]/.test(password)) score++;
    
    if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500' };
    if (score <= 3) return { score, label: 'Fair', color: 'bg-blue-500' };
    if (score <= 4) return { score, label: 'Good', color: 'bg-blue-500' };
    return { score, label: 'Strong', color: 'bg-green-500' };
  }

  // Get current password strength
  getCurrentPasswordStrength() {
    const password = this.passwordForm.get('password')?.value;
    return this.getPasswordStrength(password);
  }


  private uploadImage(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isFileUploading = true;
      
      // Store the file object for preview purposes
      this.uploadedFile = file;
      
      this.fileService.postFile(file, "USER_IMAGE_UPLOAD").subscribe({
        next: (res) => {
          console.log('UserAccountSetup: Upload successful', res);
          this.isFileUploading = false;
          const data = res as any;
          
          // Store the uploaded file URL
          this.uploadedFileUrl = data.url;
          
          // Update the form with the uploaded file URL
          this.personalInfoForm.patchValue({
            idCard: data.url
          });
          
          this.fileService.fileUploadingSubject.next({
            type: "USER_IMAGE_UPLOAD",
            loading: false,
          });

          this.messageService.add({
            severity: 'success',
            summary: 'File uploaded successfully',
            detail: `${file.name} has been uploaded`
          });
          
          resolve();
        },
        error: (error) => {
          console.error('UserAccountSetup: Upload error', error);
          this.isFileUploading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Upload failed',
            detail: error.error?.message || `Failed to upload ${file.name}`
          });
          reject(error);
        }
      });
    });
  }

  removeUploadedFile(): void {
    this.uploadedFileUrl = null;
    this.uploadedFile = null; // Clear the file object
    this.personalInfoForm.patchValue({
      idCard: null
    });
    
    // Clear the file input
    this.resetFileInput();
    
    this.messageService.add({
      severity: 'info',
      summary: 'File removed',
      detail: 'File has been removed'
    });
  }

  // Reset file input element
  private resetFileInput(): void {
    const fileInput = document.getElementById('file-input-idCard') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  ngOnDestroy() {
    // Clean up any resources if needed
    // Currently no blob URLs to clean up, but good practice to have this hook
  }

  getProgressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  // Method to check if a file is an image
  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  // Drag and drop methods
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    
    // Use centralized validation
    const validation = this.fileService.validateFile(file, 'USER_IMAGE_UPLOAD');
    if (!validation.isValid) {
      this.messageService.add({
        severity: 'error',
        summary: 'File validation failed',
        detail: validation.errors.join(', ')
      });
      return;
    }

    // Upload the file
    this.uploadImage(file).then(() => {
      // Reset file input after upload completes
      this.resetFileInput();
    }).catch(() => {
      // Reset even on error
      this.resetFileInput();
    });
  }

  triggerFileInput(): void {
    if (this.isFileUploading) return;
    
    const fileInput = document.getElementById('file-input-idCard') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileUpload(event: Event | any): void {
    // Handle both native input change event and other event types
    // In production builds, event.target might be minified, so we check multiple properties
    let files: FileList | null = null;
    
    if (event.target) {
      // Native input change event
      const target = event.target as HTMLInputElement;
      files = target.files;
    } else if (event.currentTarget) {
      // Fallback for currentTarget
      const target = event.currentTarget as HTMLInputElement;
      files = target.files;
    } else if (event.files) {
      // PrimeNG FileUpload event (if used)
      files = event.files;
    }
    
    if (files && files.length > 0) {
      const file = files[0];
      
      // Use centralized validation
      const validation = this.fileService.validateFile(file, 'USER_IMAGE_UPLOAD');
      if (!validation.isValid) {
        this.messageService.add({
          severity: 'error',
          summary: 'File validation failed',
          detail: validation.errors.join(', ')
        });
        // Reset file input on validation error
        this.resetFileInput();
        return;
      }
      
      this.uploadImage(file).then(() => {
        // Reset file input after upload completes to allow re-uploading the same file
        this.resetFileInput();
      }).catch(() => {
        // Reset even on error
        this.resetFileInput();
      });
    }
  }

  /**
   * Get the appropriate dashboard route based on user role
   * @param roleIdentification - The role identification string
   * @returns The dashboard route path
   */
  private getDashboardRouteByRole(roleIdentification?: string): string {
    switch (roleIdentification) {
      case 'FSR':
        return '/admin/fsr-dashboard';
      case 'SA':
      case 'A':
        return '/admin/admin-dashboard';
      case 'CSM':
      case 'SM':
        return '/admin/user-management';
      default:
        // Default fallback for unknown roles or undefined
        return '/admin/user-management';
    }
  }
}
